import { useState, useRef } from 'react';
import { message } from 'antd';
import { uploadSingleImage } from '../../../services/ant-design-pro/api';

interface UseImageUploadProps {
    orderNo: string;
    isConnected: boolean;
    onSendMessage: (content: string, messageType?: 'text' | 'image') => void;
}

interface UseImageUploadReturn {
    selectedImage: File | null;
    imagePreview: string | null;
    uploadProgress: number;
    fileInputRef: React.RefObject<HTMLInputElement | null>;
    handleImageSelect: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

export const useImageUpload = ({
    orderNo,
    isConnected,
    onSendMessage
}: UseImageUploadProps): UseImageUploadReturn => {
    const [selectedImage, setSelectedImage] = useState<File | null>(null);
    const [imagePreview, setImagePreview] = useState<string | null>(null);
    const [uploadProgress, setUploadProgress] = useState(0);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleImageUpload = async (file: File) => {
        if (!file || !isConnected) return;

        try {
            // 使用API服务上传图片
            const uploadResult = await uploadSingleImage(file, orderNo);
            // console.log(uploadResult);
            // 发送图片URL作为消息内容
            if (uploadResult.code === 0) {
                onSendMessage(uploadResult.data.url, 'image');
            } else {
                message.error(uploadResult.msg);
            }

        } catch (error) {
            console.error('图片上传失败:', error);

            // 上传失败时的错误处理
            let errorMessage = '图片上传失败，请重试';
            if (error instanceof Error) {
                errorMessage = error.message;
            }

            message.error(errorMessage);

            // 重置上传状态
            setUploadProgress(0);
        }
    };

    const handleImageSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file && file.type.startsWith('image/') && isConnected) {
            // 检查文件大小（限制为10MB）
            const maxSize = 10 * 1024 * 1024; // 10MB
            if (file.size > maxSize) {
                message.error('图片文件不能超过10MB');
                return;
            }

            setSelectedImage(file);

            const reader = new FileReader();
            reader.onload = async () => {
                // const imageDataUrl = e.target?.result as string;
                setImagePreview('1');
                setUploadProgress(0);

                try {
                    // 模拟上传进度
                    const progressInterval = setInterval(() => {
                        setUploadProgress(prev => {
                            if (prev >= 90) {
                                clearInterval(progressInterval);
                                return 90; // 留10%给实际上传完成
                            }
                            return prev + 10;
                        });
                    }, 200);

                    // 开始上传图片
                    await handleImageUpload(file);

                    // 清除进度更新定时器
                    clearInterval(progressInterval);

                    // 确保进度达到100%
                    setUploadProgress(100);

                    // 2秒后清除预览
                    setTimeout(() => {
                        setSelectedImage(null);
                        setImagePreview(null);
                        setUploadProgress(0);
                        if (fileInputRef.current) {
                            fileInputRef.current.value = '';
                        }
                    }, 2000);

                } catch (error) {
                    console.error('图片上传失败:', error);
                    // 错误时清除预览
                    setTimeout(() => {
                        setSelectedImage(null);
                        setImagePreview(null);
                        setUploadProgress(0);
                        if (fileInputRef.current) {
                            fileInputRef.current.value = '';
                        }
                    }, 1000);
                }
            };

            reader.readAsDataURL(file);
        } else if (!file?.type.startsWith('image/')) {
            message.error('请选择图片文件');
        } else if (!isConnected) {
            message.error('连接已断开，无法上传图片');
        }
    };

    return {
        selectedImage,
        imagePreview,
        uploadProgress,
        fileInputRef,
        handleImageSelect,
    };
}; 